import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { getSearchHistory, getHistoryCount, removeFromHistory, clearHistory } from '../utils/searchHistory.js';
import InsightsSidebar from './InsightsSidebar.jsx';
import '../styles/history-insights.css';

const HistoryInsightsView = ({ isAuthenticated }) => {
    const [history, setHistory] = useState({ keywords: [], companies: [] });
    const [selectedItem, setSelectedItem] = useState(null);
    const [historyCount, setHistoryCount] = useState({ keywords: 0, companies: 0, total: 0 });
    const [hasManualSelection, setHasManualSelection] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [hasUserClosed, setHasUserClosed] = useState(false);

    // Sidebar width & resizing state (desktop only)
    const DEFAULT_WIDTH = 500;
    const MIN_WIDTH = 100;
    const MAX_WIDTH = 700;
    const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_WIDTH);
    const [isDragging, setIsDragging] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Load history on component mount and set up refresh listener
    const loadHistory = useCallback(() => {
        const currentHistory = getSearchHistory();
        const counts = getHistoryCount();
        setHistory(currentHistory);
        setHistoryCount(counts);
    }, []);

    useEffect(() => {
        loadHistory();

        // Listen for history updates from other components
        const handleHistoryUpdate = () => {
            loadHistory();
        };

        window.addEventListener('searchHistoryUpdated', handleHistoryUpdate);
        return () => window.removeEventListener('searchHistoryUpdated', handleHistoryUpdate);
    }, [loadHistory]);

    // Auto-select the most recent item when history changes (only if no manual selection has been made)
    useEffect(() => {
        const allItems = [...history.keywords, ...history.companies];
        if (allItems.length > 0) {
            const mostRecent = allItems.reduce((latest, item) => {
                return item.timestamp > (latest?.timestamp || 0) ? item : latest;
            }, null);
            
            // Only auto-select if:
            // 1. No manual selection has been made AND
            // 2. (No current selection OR this is a newer item than currently selected)
            const shouldAutoSelect = !hasManualSelection && 
                (!selectedItem || (mostRecent && mostRecent.timestamp > (selectedItem.data?.timestamp || 0)));
            
            if (mostRecent && shouldAutoSelect) {
                // Determine if it's a keyword or company
                const isKeyword = history.keywords.includes(mostRecent);
                
                console.log("HistoryInsightsView: Auto-selecting most recent item:", mostRecent);
                
                // Clear the chart first to start fresh
                window.dispatchEvent(new CustomEvent('resetUI', { detail: { source: 'autoSelect' } }));
                
                // Temporarily create the selection without triggering manual flag
                setSelectedItem({ type: isKeyword ? 'keyword' : 'company', data: mostRecent });
                
                // Small delay to ensure chart is cleared before loading new data
                setTimeout(() => {
                    // Dispatch events to update InsightsSidebar
                    if (isKeyword) {
                        window.dispatchEvent(new CustomEvent('keywordSelected', { detail: mostRecent }));
                        window.dispatchEvent(new CustomEvent('triggerKeywordSearch', { detail: { keyword: mostRecent.keyword_name } }));
                    } else {
                        window.dispatchEvent(new CustomEvent('companySelected', { detail: mostRecent }));
                        window.dispatchEvent(new CustomEvent('triggerCompanySearch', { detail: mostRecent }));
                    }
                }, 100);
            }
        }
    }, [history, selectedItem, hasManualSelection]);

    // Handle selecting a keyword from history
    const handleKeywordSelect = useCallback((keyword) => {
        setSelectedItem({ type: 'keyword', data: keyword });
        setHasManualSelection(true); // Mark as manual selection
        
        // Clear the chart first to start fresh
        console.log("HistoryInsightsView: Clearing chart before selecting keyword:", keyword.keyword_name);
        window.dispatchEvent(new CustomEvent('resetUI', { detail: { source: 'keywordSelect' } }));
        
        // Small delay to ensure chart is cleared before loading new data
        setTimeout(() => {
            // Dispatch event to update InsightsSidebar
            console.log("HistoryInsightsView: Selecting keyword from history:", keyword);
            window.dispatchEvent(new CustomEvent('keywordSelected', { detail: keyword }));
            
            // Also trigger keyword search for chart loading
            window.dispatchEvent(new CustomEvent('triggerKeywordSearch', { detail: { keyword: keyword.keyword_name } }));
        }, 100);
    }, []);

    // Handle selecting a company from history
    const handleCompanySelect = useCallback((company) => {
        setSelectedItem({ type: 'company', data: company });
        setHasManualSelection(true); // Mark as manual selection
        
        // Clear the chart first to start fresh
        console.log("HistoryInsightsView: Clearing chart before selecting company:", company.symbol);
        window.dispatchEvent(new CustomEvent('resetUI', { detail: { source: 'companySelect' } }));
        
        // Small delay to ensure chart is cleared before loading new data
        setTimeout(() => {
            // Dispatch event to update InsightsSidebar
            console.log("HistoryInsightsView: Selecting company from history:", company);
            window.dispatchEvent(new CustomEvent('companySelected', { detail: company }));
            
            // Also trigger company search for chart loading
            window.dispatchEvent(new CustomEvent('triggerCompanySearch', { detail: company }));
        }, 100);
    }, []);

    // Handle removing an item from history
    const handleRemoveItem = useCallback((type, item) => {
        const id = type === 'keyword' ? item.id : item.company_id;
        removeFromHistory(type === 'keyword' ? 'keywords' : 'companies', id);
        
        // If the removed item was selected, clear selection and reset manual flag
        if (selectedItem && 
            ((type === 'keyword' && selectedItem.data.id === id) ||
             (type === 'company' && selectedItem.data.company_id === id))) {
            setSelectedItem(null);
            setHasManualSelection(false);
            window.dispatchEvent(new CustomEvent('resetUI', { detail: { source: 'historyRemove' } }));
        }
        
        loadHistory();
    }, [selectedItem, loadHistory]);

    // Handle clearing all history
    const handleClearHistory = useCallback(() => {
        clearHistory();
        setSelectedItem(null);
        setHasManualSelection(false); // Reset manual selection flag
        loadHistory();
        window.dispatchEvent(new CustomEvent('resetUI', { detail: { source: 'historyClear' } }));
    }, [loadHistory]);

    // --- Sidebar Toggle Handlers ---
    const toggleSidebar = useCallback(() => {
        setIsOpen(prev => {
            const next = !prev;
            setHasUserClosed(!next);
            return next;
        });
    }, []);

    const closeSidebar = useCallback(() => {
        console.log('HistoryInsightsView: Closing sidebar');
        setHasUserClosed(true);
        setIsOpen(false);
    }, []);

    // Auto-open sidebar when history has items
    useEffect(() => {
        if (historyCount.total > 0 && !isOpen && !hasUserClosed) {
            setIsOpen(true);
        }
    }, [historyCount.total, isOpen, hasUserClosed]);

    // Add/remove body class when sidebar opens/closes
    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('sidebar-open');
        } else {
            document.body.classList.remove('sidebar-open');
        }
        
        // Cleanup on unmount
        return () => {
            document.body.classList.remove('sidebar-open');
        };
    }, [isOpen]);

    // Reflect current sidebar width to a global CSS variable so pages can adjust layout
    useEffect(() => {
        const applyWidthVar = () => {
            const targetWidth = isOpen && !isMobile
                ? Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, sidebarWidth))
                : 0;
            try {
                document.documentElement.style.setProperty('--history-sidebar-width', `${targetWidth}px`);
            } catch (_) {}
        };
        applyWidthVar();
        return () => {
            // On unmount, reset to 0 to avoid affecting other pages
            try { document.documentElement.style.setProperty('--history-sidebar-width', '0px'); } catch (_) {}
        };
    }, [isOpen, isMobile, sidebarWidth]);

    // Initialize width and mobile flag
    useEffect(() => {
        // In development, check if DEFAULT_WIDTH changed and reset if needed
        if (process.env.NODE_ENV === 'development') {
            const lastDefaultWidth = localStorage.getItem('lastDefaultWidth');
            if (lastDefaultWidth && parseInt(lastDefaultWidth) !== DEFAULT_WIDTH) {
                localStorage.removeItem('historySidebarWidth');
                console.log('🔧 Dev: Sidebar width reset due to DEFAULT_WIDTH change');
            }
            localStorage.setItem('lastDefaultWidth', String(DEFAULT_WIDTH));
        }
        
        const stored = parseInt(localStorage.getItem('historySidebarWidth'), 10);
        if (!isNaN(stored)) {
            setSidebarWidth(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, stored)));
        }

        const updateMobile = () => setIsMobile(window.innerWidth <= 768);
        updateMobile();
        window.addEventListener('resize', updateMobile);
        return () => window.removeEventListener('resize', updateMobile);
    }, []);

    // Clamp width if window gets too small
    useEffect(() => {
        if (isMobile) return;
        const maxAllowed = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - 120));
        if (sidebarWidth > maxAllowed) setSidebarWidth(maxAllowed);
    }, [isMobile, sidebarWidth]);

    // Drag handlers (left edge of right-fixed sidebar)
    const handleResizeMouseDown = useCallback((e) => {
        if (isMobile) return; // disable on mobile
        setIsDragging(true);
        e.preventDefault();
        e.stopPropagation();

        const onMouseMove = (ev) => {
            // Width = distance from cursor to right edge of viewport
            const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, window.innerWidth - ev.clientX));
            setSidebarWidth(newWidth);
            try { document.documentElement.style.setProperty('--history-sidebar-width', `${newWidth}px`); } catch (_) {}
        };

        const onMouseUp = () => {
            setIsDragging(false);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            try { localStorage.setItem('historySidebarWidth', String(sidebarWidth)); } catch (_) {}
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [isMobile, sidebarWidth]);

    // Keyboard support for closing sidebar
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && isOpen) {
                closeSidebar();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isOpen, closeSidebar]);

    // Format timestamp for display
    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        
        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        return date.toLocaleDateString();
    };

    // Render compact item chip
    const renderHistoryItem = (item, type, onSelect, onRemove) => {
        const isSelected = selectedItem && 
            ((type === 'keyword' && selectedItem.data.id === item.id) ||
             (type === 'company' && selectedItem.data.company_id === item.company_id));

        return (
            <div
                key={item.id || item.company_id}
                className={`history-item inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-all duration-200 ${isSelected ? 'selected' : ''}`}
                title={type === 'keyword' ? item.keyword_name : `${item.symbol}${item.name && item.name !== item.symbol ? ` - ${item.name}` : ''}`}
            >
                {/* Content */}
                {type === 'keyword' ? (
                    <span 
                        className="item-label font-medium truncate max-w-24 cursor-pointer transition-colors"
                        onClick={() => onSelect(item)}
                    >
                        {item.keyword_name}
                    </span>
                ) : (
                    <div 
                        className="flex items-center gap-1 min-w-0 cursor-pointer transition-colors"
                        onClick={() => onSelect(item)}
                    >
                        <span className="font-medium flex-shrink-0">
                            {item.symbol}
                        </span>
                        {item.name && item.name !== item.symbol && (
                            <span className="text-gray-400 truncate max-w-20 text-[10px] item-label">
                                {item.name}
                            </span>
                        )}
                    </div>
                )}

                {/* Remove button */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove(type, item);
                    }}
                    className="history-remove-button flex-shrink-0 p-0.5 ml-1"
                    aria-label={`Remove ${type === 'keyword' ? item.keyword_name : item.symbol} from history`}
                >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </button>
            </div>
        );
    };

    return (
        <>
            {/* Single Toggle/Close Button */}
            {(typeof document !== 'undefined' && createPortal(
                <button 
                    type="button"
                    className={`history-toggle ${isOpen ? 'open' : ''}`}
                    onClick={(e) => { e.stopPropagation(); toggleSidebar(); }}
                    title={isOpen ? "Close Sidebar" : "Open Sidebar"}
                    aria-label={isOpen ? "Close Sidebar" : "Open Sidebar"}
                    style={{ pointerEvents: 'auto' }}
                >
                    {isOpen ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12"/>
                        </svg>
                    ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 12h18M3 6h18M3 18h18"/>
                        </svg>
                    )}
                </button>,
                document.body
            )) || (
                <button 
                    type="button"
                    className={`history-toggle ${isOpen ? 'open' : ''}`}
                    onClick={(e) => { e.stopPropagation(); toggleSidebar(); }}
                    title={isOpen ? "Close Sidebar" : "Open Sidebar"}
                    aria-label={isOpen ? "Close Sidebar" : "Open Sidebar"}
                >
                    {isOpen ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12"/>
                        </svg>
                    ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 12h18M3 6h18M3 18h18"/>
                        </svg>
                    )}
                </button>
            )}

            {/* Backdrop (mobile only) */}
            <div 
                className={`history-backdrop ${isOpen ? 'visible' : ''}`}
                onClick={(e) => { e.stopPropagation(); closeSidebar(); }}
                role="button"
                aria-label="Close Sidebar Backdrop"
            />

            {/* History Sidebar */}
            <div 
                className={`history-insights-view ${isOpen ? 'open' : ''}`}
                style={{ width: isMobile ? undefined : `${Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, sidebarWidth))}px` }}
            >

                {/* Resize handle (left edge) - desktop only */}
                {!isMobile && (
                    <div 
                        className={`history-resize-handle${isDragging ? ' dragging' : ''}`}
                        onMouseDown={handleResizeMouseDown}
                        role="separator"
                        aria-orientation="vertical"
                        aria-label="Resize sidebar"
                        title="Drag to resize"
                    />
                )}

                {/* Scrollable Content Container */}
                <div className="sidebar-scrollable-content">
                    {/* History Bar - Horizontal at top */}
                    <div className="history-bar bg-white/5 border-b border-white/10 p-2">
                        {/* Header with Clear All button */}
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                                <div style={{
                                    width: '4px',
                                    height: '20px',
                                    background: 'linear-gradient(180deg, #ffffff, #9ca3af)',
                                    borderRadius: '2px'
                                }} />
                                <h2 style={{
                                    fontSize: '1.1rem',
                                    fontWeight: '600',
                                    color: '#ffffff',
                                    letterSpacing: '0.025em',
                                    textTransform: 'uppercase'
                                }}>
                                    Recent Activity
                                </h2>
                            </div>
                            {historyCount.total > 0 && (
                                <button
                                    onClick={handleClearHistory}
                                    className="text-xs text-gray-400 hover:text-red-400 transition-colors font-medium"
                                >
                                    Clear All
                                </button>
                            )}
                        </div>

                {/* History Content */}
                {historyCount.total === 0 ? (
                    <div className="text-center py-8">
                        <div className="text-gray-500 text-sm mb-2">No search history yet</div>
                        <div className="text-gray-600 text-xs">
                            Search for keywords or companies to see them here
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-4">
                        {/* Keywords Section - Left Side */}
                        <div className="keywords-section">
                            <div className="flex items-center gap-2 mb-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                </svg>
                                <h3 className="text-sm font-medium text-gray-300">
                                    Keywords ({history.keywords.length})
                                </h3>
                            </div>
                            <div className="max-h-32 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent history-list">
                                {history.keywords.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {history.keywords.map((keyword) => 
                                            renderHistoryItem(keyword, 'keyword', handleKeywordSelect, handleRemoveItem)
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-gray-500 text-xs italic py-2">No keywords yet</div>
                                )}
                            </div>
                        </div>

                        {/* Companies Section - Right Side */}
                        <div className="companies-section">
                            <div className="flex items-center gap-2 mb-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <h3 className="text-sm font-medium text-gray-300">
                                    Companies ({history.companies.length})
                                </h3>
                            </div>
                            <div className="max-h-32 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent history-list">
                                {history.companies.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {history.companies.map((company) => 
                                            renderHistoryItem(company, 'company', handleCompanySelect, handleRemoveItem)
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-gray-500 text-xs italic py-2">No companies yet</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                    </div>

                    {/* Insights Panel - Stacked below history */}
                    <div className="insights-panel-stacked">
                        <InsightsSidebar 
                            isAuthenticated={isAuthenticated} 
                            isStacked={true}
                            parentIsOpen={isOpen}
                            onParentClose={closeSidebar}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

HistoryInsightsView.propTypes = {
    isAuthenticated: PropTypes.bool.isRequired
};

export default HistoryInsightsView; 